import type { ChallengeCreateFormValues } from '@/forms/challenge-create-form/ChallengeCreateForm';
import { ChallengeType } from '@/forms/challenge-create-form/types';
import {
  ChallengeCriteria,
  ChallengeExternalSystem,
  ChallengeRewardInputV2,
  ChallengeRewardTypeEnum,
  ChallengeRewardTypeInput,
  ClaimType,
} from '@/globalTypes';
import { GameOption } from '@/hooks/useGamesSearch';
import {
  getCloneDates,
  getCloneName,
  getGameDescriptorJson,
  getGameDescriptorProviderName,
  toGameProvider,
} from '@/utils/clone';
import {
  parseDurationToSeconds,
  secondsToUnitDurationString,
} from '@/utils/parse-duration';
import { ChallengeDetailQuery } from './__generated__/useChallengeDetail';

export type ChallengeDetail = NonNullable<
  NonNullable<
    NonNullable<ChallengeDetailQuery['viewer']['challenges']['edges']>[number]
  >['node']
>;

type ChallengeReward = ChallengeDetail['challengeRewards'][number];
type ChallengeRewardType = ChallengeReward['challengeRewardType'];

// Defaults of the challenge reward create form
const DEFAULT_ACTIVATE_AFTER = '1.second';
const DEFAULT_EXPIRES_IN = '7.days';

export const getRewardGameJson = (rewardType: ChallengeRewardType) =>
  'gameDescriptor' in rewardType
    ? getGameDescriptorJson(rewardType.gameDescriptor)
    : undefined;

const toUnitDurationString = (value: unknown, fallback: string) => {
  const seconds = parseDurationToSeconds(value);
  return seconds ? secondsToUnitDurationString(seconds) : fallback;
};

const getGameInput = (
  rewardType: ChallengeRewardType,
  game?: GameOption | null,
) => {
  const provider = toGameProvider(
    game?.providerName,
    getGameDescriptorProviderName(getRewardGameJson(rewardType)),
  );

  if (!game || !provider || (!game.mobileId && !game.desktopId)) {
    return null;
  }

  return {
    provider,
    mobileId: game.mobileId,
    desktopId: game.desktopId,
  };
};

/**
 * Maps a challenge reward from the API to the input expected by the create
 * mutations, mirroring `getRewardTypeInput` of the challenge reward form.
 * Returns `null` when the reward can't be recreated (e.g. unknown game).
 */
const mapRewardTypeInput = (
  rewardType: ChallengeRewardType,
  game?: GameOption | null,
): ChallengeRewardTypeInput | null => {
  switch (rewardType.__typename) {
    case 'MoneyDropChallengeReward':
      return {
        challengeRewardType: ChallengeRewardTypeEnum.MoneyDropType,
        moneyDrop: {
          amount: Number(rewardType.moneyDropAmount),
          currency: rewardType.currency,
        },
      };
    case 'BonusMoneyDropChallengeReward':
      return {
        challengeRewardType: ChallengeRewardTypeEnum.BonusMoneyDropType,
        bonusMoneyDrop: {
          amount: Number(rewardType.bonusMoneyDropAmount),
          currency: rewardType.currency,
          wageringRequirementFactor: rewardType.wageringRequirementFactor,
          maxPayout: Number(rewardType.maxPayout),
        },
      };
    case 'DynamicMoneyDropChallengeReward':
      return {
        challengeRewardType: ChallengeRewardTypeEnum.DynamicMoneyDropType,
        dynamicMoneyDrop: {
          multiplier: Number(rewardType.multiplier),
          maxAmount: Number(rewardType.maxAmount),
          currency: rewardType.currency,
        },
      };
    case 'DynamicBonusMoneyDropChallengeReward':
      return {
        challengeRewardType: ChallengeRewardTypeEnum.DynamicBonusMoneyDropType,
        dynamicBonusMoneyDrop: {
          multiplier: Number(rewardType.multiplier),
          maxAmount: Number(rewardType.maxAmount),
          currency: rewardType.currency,
          wageringRequirementFactor: rewardType.wageringRequirementFactor,
          maxPayout: Number(rewardType.maxPayout),
          maxPayoutMultiplier:
            rewardType.maxPayoutMultiplier != null
              ? Number(rewardType.maxPayoutMultiplier)
              : null,
        },
      };
    case 'FreeSpinChallengeReward': {
      const gameInput = getGameInput(rewardType, game);
      if (!gameInput) {
        return null;
      }

      return {
        challengeRewardType: ChallengeRewardTypeEnum.FreeSpinsType,
        freeSpins: {
          ...gameInput,
          amountFreeSpins: rewardType.freeSpinsAmount,
          freeSpinValue: Number(rewardType.value),
          payLines: rewardType.betLevel,
          currency: rewardType.currency,
        },
      };
    }
    case 'DynamicFreeSpinChallengeReward': {
      const gameInput = getGameInput(rewardType, game);
      if (!gameInput) {
        return null;
      }

      return {
        challengeRewardType: ChallengeRewardTypeEnum.DynamicFreeSpinsType,
        dynamicFreeSpins: {
          ...gameInput,
          multiplier: Number(rewardType.multiplier),
          maxFreeSpinsAmount: rewardType.maxFreeSpinsAmount,
          freeSpinValue: Number(rewardType.value),
          payLines: rewardType.betLevel,
          currency: rewardType.currency,
        },
      };
    }
    default:
      return null;
  }
};

const getRewardCurrency = (rewardType: ChallengeRewardType) =>
  'currency' in rewardType ? rewardType.currency : undefined;

/**
 * Maps a challenge from the API to the challenge create form values.
 * `games` holds the resolved game of each reward, by index.
 * Rewards which can't be recreated are listed in `skippedRewards`.
 */
export const mapChallengeToFormValues = (
  challenge: ChallengeDetail,
  games: (GameOption | null | undefined)[] = [],
  now = new Date(),
) => {
  const rewards: ChallengeRewardInputV2[] = [];
  const skippedRewards: string[] = [];

  challenge.challengeRewards.forEach((reward, index) => {
    const challengeRewardTypeInput = mapRewardTypeInput(
      reward.challengeRewardType,
      games[index],
    );

    if (!challengeRewardTypeInput) {
      skippedRewards.push(reward.challengeRewardType.name || 'N/A');
      return;
    }

    rewards.push({
      challengeRewardTypeInput,
      activateAfter: toUnitDurationString(
        reward.activateAfter,
        DEFAULT_ACTIVATE_AFTER,
      ),
      expiresIn: toUnitDurationString(reward.expiresIn, DEFAULT_EXPIRES_IN),
      // Not returned by the API, the default of the reward form is used
      claimType: ClaimType.Manual,
    });
  });

  const challengeType = challenge.challengeActions.includes(
    ChallengeCriteria.CompleteSoW,
  )
    ? ChallengeType.CompleteSoW
    : ChallengeType.Deposit;

  const challengeSystem = Object.values(ChallengeExternalSystem).find(
    (system) => system === challenge.challengeSystem,
  );

  const currencyCode = challenge.challengeRewards
    .map((reward) => getRewardCurrency(reward.challengeRewardType))
    .find(Boolean);

  const { startsAt, endsAt } = getCloneDates(
    challenge.startDate,
    challenge.endDate,
    now,
  );

  const values: Partial<ChallengeCreateFormValues> = {
    challengeName: getCloneName(challenge.name),
    brands: challenge.brands,
    countries: challenge.countries,
    challengeType,
    rewards,
    startsAt,
    endsAt,
    ...(challengeSystem && { challengeSystem }),
    ...(currencyCode && { currencyCode }),
  };

  return { values, skippedRewards };
};
